import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  fetchPublicConfig,
  linkTelegram,
  startDiscordOAuth,
  startXOAuth,
  unlinkDiscord,
  unlinkTelegram,
  unlinkX,
  type TelegramAuthPayload,
} from '@/lib/api';

const TG_WIDGET_SCRIPT_SRC = 'https://telegram.org/js/telegram-widget.js?22';
const TG_CALLBACK_NAME = '__bigwOnTelegramAuth';

declare global {
  interface Window {
    [TG_CALLBACK_NAME]?: (user: TelegramAuthPayload) => void;
  }
}

/**
 * Mounts the official Telegram Login Widget. The widget script handles the
 * full lifecycle: iframe creation, origin negotiation, NOT_AUTHORIZED
 * fallback HTML, popup auth, and the postMessage flow back to our
 * data-onauth callback. We tried embedding the iframe ourselves to avoid
 * the script's brief width-0 → resize slide-in, but bypassing the wrapper
 * means we don't get the fallback button when the embed responds with
 * NOT_AUTHORIZED — so the user clicks nothing and auth never starts.
 *
 * `enabled` should be true only when the host element is actually rendered
 * (e.g. modal open, account not yet linked). The effect re-runs on toggle
 * so the iframe is recreated each time the user opens the modal.
 */
export function useTelegramLoginWidget(
  containerRef: React.RefObject<HTMLDivElement>,
  enabled: boolean,
  onLinked?: () => void,
) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  const onLinkedRef = useRef(onLinked);
  onLinkedRef.current = onLinked;

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let scriptEl: HTMLScriptElement | null = null;
    let mountedContainer: HTMLDivElement | null = null;
    let pollId: number | null = null;

    const handler = async (user: TelegramAuthPayload) => {
      setLinking(true);
      setError(null);
      try {
        await linkTelegram(user);
        qc.invalidateQueries({ queryKey: ['me'] });
        onLinkedRef.current?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to link Telegram');
      } finally {
        setLinking(false);
      }
    };
    window[TG_CALLBACK_NAME] = handler;

    const mount = (botUsername: string) => {
      const container = containerRef.current;
      if (!container) return false;
      mountedContainer = container;
      container.innerHTML = '';
      const script = document.createElement('script');
      script.async = true;
      script.src = TG_WIDGET_SCRIPT_SRC;
      script.setAttribute('data-telegram-login', botUsername);
      script.setAttribute('data-size', 'medium');
      script.setAttribute('data-userpic', 'false');
      script.setAttribute('data-onauth', `${TG_CALLBACK_NAME}(user)`);
      script.setAttribute('data-request-access', 'write');
      script.onerror = () =>
        setError('Telegram widget failed to load. Check the bot domain in @BotFather.');
      container.appendChild(script);
      scriptEl = script;
      return true;
    };

    fetchPublicConfig()
      .then((cfg) => {
        if (cancelled) return;
        if (!cfg?.telegram_bot_username) {
          setError('Telegram disabled on the server');
          return;
        }
        // The container may not be in the DOM yet on the first effect tick
        // (modal animation, conditional render). Poll briefly for it.
        if (mount(cfg.telegram_bot_username)) return;
        let attempts = 0;
        pollId = window.setInterval(() => {
          attempts += 1;
          if (mount(cfg.telegram_bot_username) || attempts > 20) {
            if (pollId !== null) {
              window.clearInterval(pollId);
              pollId = null;
            }
          }
        }, 50);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load config');
        }
      });

    return () => {
      cancelled = true;
      if (pollId !== null) window.clearInterval(pollId);
      if (scriptEl?.parentNode) scriptEl.parentNode.removeChild(scriptEl);
      // Also clean up any iframe Telegram injected next to the script.
      if (mountedContainer) mountedContainer.innerHTML = '';
      delete window[TG_CALLBACK_NAME];
    };
  }, [enabled, containerRef, qc]);

  return { error, linking };
}

export function useUnlinkTelegram() {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => unlinkTelegram(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

// How long "Redirecting…" may last while the page is still here. Browsers
// fire no event when a navigation to another site fails to start (provider
// unreachable, blocked, very slow), so this is the fallback.
const REDIRECT_TIMEOUT_MS = 12_000;

/**
 * Starts an OAuth link (X / Discord): asks bigw-api for the provider's
 * authorize URL, then leaves the page for it. A successful start means
 * "navigating away", so `pending` stays true after it — but only while the
 * page is actually gone. If the page comes back instead (Back from the
 * provider and a back/forward-cache restore, the provider's app took over
 * the link, an in-app browser opened it elsewhere) or never leaves, the
 * button resets rather than staying on "Redirecting…" until a reload.
 */
function useOAuthRedirect(startOAuth: () => Promise<string>, label: string) {
  const qc = useQueryClient();
  const [openError, setOpenError] = useState<string | null>(null);
  const { mutate, reset, isPending, isSuccess, error } = useMutation<string, Error, void>({
    mutationFn: () => startOAuth(),
    onSuccess: (url) => window.location.assign(url),
  });

  useEffect(() => {
    if (!isSuccess) return;

    const timer = window.setTimeout(() => {
      reset();
      setOpenError(`Couldn't open ${label}. Please try again.`);
    }, REDIRECT_TIMEOUT_MS);

    const onReturn = () => {
      // 'hidden' is the page leaving as planned — only a return counts.
      if (document.visibilityState !== 'visible') return;
      reset();
      // The link may have finished elsewhere (another browser or app).
      qc.invalidateQueries({ queryKey: ['me'] });
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('pageshow', onReturn);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('pageshow', onReturn);
    };
  }, [isSuccess, label, reset, qc]);

  const start = useCallback(() => {
    setOpenError(null);
    mutate();
  }, [mutate]);

  return {
    start,
    pending: isPending || isSuccess,
    error: openError ?? error?.message ?? null,
  };
}

export function useLinkDiscord() {
  return useOAuthRedirect(startDiscordOAuth, 'Discord');
}

export function useUnlinkDiscord() {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => unlinkDiscord(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useLinkX() {
  return useOAuthRedirect(startXOAuth, 'X');
}

export function useUnlinkX() {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => unlinkX(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

/**
 * Reads the ?discord=ok|error&reason=... query string the backend appends
 * after the OAuth callback redirects back to the frontend, and clears it
 * from the URL once consumed. Returns the latest status so callers can
 * surface a toast / inline message.
 */
function useOAuthCallbackStatus(param: 'discord' | 'x'): {
  status: 'ok' | 'error' | null;
  reason: string | null;
} {
  const [state, setState] = useState<{ status: 'ok' | 'error' | null; reason: string | null }>({
    status: null,
    reason: null,
  });
  const qc = useQueryClient();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get(param);
    if (!status) return;
    if (status === 'ok' || status === 'error') {
      setState({ status, reason: params.get('reason') });
      if (status === 'ok') qc.invalidateQueries({ queryKey: ['me'] });
    }
    params.delete(param);
    params.delete('reason');
    const newSearch = params.toString();
    const newUrl =
      window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
    window.history.replaceState({}, '', newUrl);
  }, [qc, param]);

  return state;
}

export function useDiscordCallbackStatus(): {
  discord: 'ok' | 'error' | null;
  reason: string | null;
} {
  const { status, reason } = useOAuthCallbackStatus('discord');
  return { discord: status, reason };
}

export function useXCallbackStatus(): { x: 'ok' | 'error' | null; reason: string | null } {
  const { status, reason } = useOAuthCallbackStatus('x');
  return { x: status, reason };
}
